import { useState, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Building2, PartyPopper, Boxes, HandHeart } from "lucide-react";
import { CaptchaBox, CAPTCHA_ENABLED } from "@/components/CaptchaBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  event_type: "",
  event_date: "",
  guest_count: "",
  message: "",
};

const perks = [
  { icon: Building2, title: "Corporate orders", text: "Office mornings, client meetings & team treats — delivered fresh." },
  { icon: PartyPopper, title: "Events & celebrations", text: "Weddings, birthdays, school functions and grand openings." },
  { icon: Boxes, title: "Bulk & wholesale", text: "Dozens by the dozen, with friendly pricing for big orders." },
  { icon: HandHeart, title: "Give back built in", text: "Every order helps fuel our mission of inclusion in the community." },
];

export const Quote = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const captchaRef = useRef(null);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email, and a few details.");
      return;
    }
    if (CAPTCHA_ENABLED && !token) {
      toast.error("Please complete the CAPTCHA below.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, turnstile_token: token });
      toast.success("Quote request sent! We'll get back to you with pricing soon. 🍩");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
      captchaRef.current?.reset();
      setToken("");
    }
  };

  const field =
    "w-full rounded-2xl border-2 border-[#e8dcc8] bg-[#f7f0eb] px-4 py-3 text-[#111111] placeholder-[#3d3d3d]/50 focus:outline-none focus:border-[#ff648b] focus:ring-2 focus:ring-[#ff648b]/40 transition";

  return (
    <section id="quote" data-testid="quote-section" className="py-20 md:py-32 px-5 md:px-10 bg-[#ff648b]">
      <div className="max-w-7xl mx-auto">
        {/* image + text — always side by side (image hidden on mobile, scales with screen width) */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* donut tower — iPad and up, left of the text */}
          <motion.img
            src="/donut-tower.png?v=6"
            alt="A comically tall, leaning tower of Bobo's donuts on a serving plate"
            data-testid="quote-donut-tower"
            loading="lazy"
            animate={{ rotate: [-1.5, 1.5, -1.5] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "bottom center" }}
            className="hidden md:order-last md:block justify-self-center self-center w-auto max-h-[40vw] 2xl:max-h-[40rem] drop-shadow-2xl select-none"
          />

          {/* pitch */}
          <div className="md:pl-10 xl:pl-20">
          <h2 className="font-display font-bold text-4xl sm:text-5xl text-[#111111] tracking-tight leading-[1.02]">
            Get a quote for your next big order
          </h2>
          <p className="text-lg text-[#111111]/85 mt-5 leading-relaxed max-w-lg">
            Planning something sweet? Tell us about your corporate gathering or
            event and we'll send back custom pricing. No order is too big — or
            too pink.
          </p>

          <div className="grid sm:grid-cols-2 gap-5 mt-9">
            {perks.map((p) => (
              <div key={p.title} className="flex gap-3.5 items-start">
                <div className="shrink-0 w-11 h-11 rounded-2xl bg-[#f7f0eb] flex items-center justify-center shadow">
                  <p.icon className="h-5 w-5 text-[#111111]" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-lg text-[#111111] leading-tight">{p.title}</h3>
                  <p className="text-[#111111]/75 text-sm mt-1">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>

        {/* form — centered beneath the image and text */}
        <form
          data-testid="quote-form"
          onSubmit={submit}
          className="mt-12 sm:mt-16 max-w-2xl mx-auto bg-white rounded-3xl p-7 sm:p-9 shadow-2xl border-2 border-white/60 space-y-4"
        >
          <h3 className="font-display font-bold text-2xl text-[#111111]">Request a quote</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="quote-name" name="name" value={form.name} onChange={change} placeholder="Your name *" className={field} />
            <input data-testid="quote-company" name="company" value={form.company} onChange={change} placeholder="Company / org" className={field} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="quote-email" name="email" type="email" value={form.email} onChange={change} placeholder="Email *" className={field} />
            <input data-testid="quote-phone" name="phone" value={form.phone} onChange={change} placeholder="Phone" className={field} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="quote-event-type" name="event_type" value={form.event_type} onChange={change} className={field}>
              <option value="">Order type…</option>
              <option value="Corporate / Office">Corporate / Office</option>
              <option value="Wedding">Wedding</option>
              <option value="Birthday / Party">Birthday / Party</option>
              <option value="School / Community">School / Community</option>
              <option value="Bulk / Wholesale">Bulk / Wholesale</option>
              <option value="Other">Other</option>
            </select>
            <input data-testid="quote-guests" name="guest_count" value={form.guest_count} onChange={change} placeholder="Approx. quantity / guests" className={field} />
          </div>
          <input data-testid="quote-date" name="event_date" value={form.event_date} onChange={change} placeholder="Event date (optional)" className={field} />
          <textarea data-testid="quote-message" name="message" value={form.message} onChange={change} placeholder="Tell us what you have in mind *" rows={4} className={field} />

          <CaptchaBox ref={captchaRef} onToken={setToken} />

          <button
            data-testid="quote-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-full bg-[#111111] text-[#f7f0eb] font-bold text-lg hover:bg-[#000000] hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            {loading ? "Sending..." : "Request My Quote"}
          </button>
        </form>
      </div>
    </section>
  );
};
